// v2/pages/OrdersV2.js — Siparislerim (B2B siparisleri, sunucu tarafli sayfalama)
// URL: ?q= &period=all|month|3m|6m|year|custom &from= &to= &wh=gzt|ist &status= &page= &per=
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Search, X, Package, Calendar, Warehouse, ClipboardList, Wallet, TrendingUp,
  TrendingDown, CalendarDays, Layers, Inbox, ChevronDown, ChevronUp,
  ChevronLeft, ChevronRight, Filter, FileText, MessageSquare, RotateCw, Clock, XCircle,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ToastStack from '../components/Toast';
import useToast from '../hooks/useToast';
import useMenuNav from '../hooks/useMenuNav';
import useCartContext from '../hooks/CartContext';
import useExchangeRates from '../hooks/useExchangeRates';
import ProductThumb from '../components/ProductThumb';
import { DiscountChips } from '../components/ProductRow';
import { useOrders, useOrderSummary } from '../hooks/useOrders';
import { formatTRY, formatNumber } from '../utils/format';
import { VAT_RATE } from '../utils/discount';
import styles from '../styles/DashboardV2.module.css';
import css from '../styles/OrdersV2.module.css';
import '../styles/tokens.css';

const ORDER_STATUS = {
  processing:  { label: 'İşleniyor',       tone: 'stAmber' },
  transferred: { label: 'Transfer Edildi', tone: 'stGreen' },
  error:       { label: 'Aktarım Hatası',  tone: 'stRed' },
};

const PERIODS = [
  { id: 'all', label: 'Tümü' },
  { id: 'month', label: 'Bu Ay' },
  { id: '3m', label: 'Son 3 Ay' },
  { id: '6m', label: 'Son 6 Ay' },
  { id: 'year', label: 'Bu Yıl' },
  { id: 'custom', label: 'Tarih Aralığı' },
];
const WH_F = [
  { id: '', label: 'Tüm Depolar' },
  { id: 'gzt', label: 'Gaziantep' },
  { id: 'ist', label: 'İstanbul' },
];
const STATUS_F = [
  { id: '', label: 'Tüm Durumlar' },
  ...Object.entries(ORDER_STATUS).map(([id, v]) => ({ id, label: v.label })),
];
const PER_PAGE = [10, 20, 50];
const LINE_PREVIEW = 3;
const CHART_MONTHS = 8;
const TARIH_RX = /^\d{4}-\d{2}-\d{2}$/;

const MONTHS_LONG = [
  'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
];
const fmtDate = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
};
const fmtDateLong = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${parseInt(d, 10)} ${MONTHS_LONG[parseInt(m, 10) - 1]} ${y}`;
};
const depoMetni = (list) => (list.length ? list.join(' + ') : '—');
const urunLinki = (code) => `/v2/products?q=${encodeURIComponent(code)}`;

// ---------- URL <-> filtre ----------
const BOS = { q: '', period: 'all', from: '', to: '', wh: '', status: '', page: 1, per: PER_PAGE[0] };
const URL_ALAN = ['q', 'period', 'from', 'to', 'wh', 'status', 'page', 'per'];

function duzelt(f) {
  const n = { ...BOS, ...f };
  n.q = String(n.q || '').slice(0, 60);
  if (PERIODS.some((p) => p.id === n.period) === false) n.period = 'all';
  if (WH_F.some((w) => w.id === n.wh) === false) n.wh = '';
  if (STATUS_F.some((s) => s.id === n.status) === false) n.status = '';
  if (n.period !== 'custom') { n.from = ''; n.to = ''; }
  if (TARIH_RX.test(n.from) === false) n.from = '';
  if (TARIH_RX.test(n.to) === false) n.to = '';
  n.page = Math.max(parseInt(n.page, 10) || 1, 1);
  n.per = PER_PAGE.includes(Number(n.per)) ? Number(n.per) : BOS.per;
  return n;
}

function urldenOku(sp) {
  const f = {};
  URL_ALAN.forEach((k) => {
    const v = sp.get(k);
    if (v !== null) f[k] = v;
  });
  return duzelt(f);
}

function urleYaz(f) {
  const p = new URLSearchParams();
  URL_ALAN.forEach((k) => {
    const v = f[k];
    if (v === '' || v === null || v === undefined || v === BOS[k]) return;
    p.set(k, String(v));
  });
  return p.toString();
}

// ---------- parcalar ----------
function Thumb({ code, alt, className }) {
  return (
    <ProductThumb code={code} name={alt} className={className} imgClassName={css.thumbImg} />
  );
}

function DepoEtiketleri({ line }) {
  return (
    <>
      {line.qtyGzt > 0 && <span className={css.whTag}>GZT</span>}
      {line.qtyIst > 0 && <span className={css.whTag}>İST</span>}
    </>
  );
}

function IndirimHucre({ line }) {
  if (line.discounts.length > 0 && line.discountExact) {
    return (
      <span className={css.dChain} title={`Toplam indirim %${formatNumber(line.discountRate, 2)}`}>
        <DiscountChips discounts={line.discounts} />
      </span>
    );
  }
  if (line.discountRate > 0) {
    return (
      <span className={css.dDisc} title="Toplam indirim (zincir bilgisi bulunamadı)">
        %{formatNumber(line.discountRate, 2)}
      </span>
    );
  }
  return <span className={css.dNone}>—</span>;
}

function StatCards({ ozet }) {
  const y = ozet ? ozet.year : null;
  const m = ozet ? ozet.month : null;
  const items = [
    { key: 'o', Icon: ClipboardList, cls: css.iconBlue,
      label: y ? `${y.year} Siparişleri` : 'Bu Yıl Sipariş',
      value: y ? formatNumber(y.orders) : '—', unit: 'adet' },
    { key: 'a', Icon: Wallet, cls: css.iconGreen,
      label: 'Bu Yıl Tutar', value: y ? formatTRY(y.amount) : '—' },
    { key: 'm', Icon: CalendarDays, cls: css.iconAmber,
      label: 'Bu Ay', value: m ? formatNumber(m.orders) : '—', unit: 'sipariş' },
    { key: 'v', Icon: TrendingUp, cls: css.iconSlate,
      label: 'Ortalama Sipariş', value: y ? formatTRY(y.average) : '—' },
  ];
  return (
    <div className={css.statGrid}>
      {items.map(({ key, Icon, cls, label, value, unit }) => (
        <div key={key} className={css.statCard}>
          <span className={`${css.statIcon} ${cls}`}><Icon size={18} /></span>
          <span className={css.statBody}>
            <span className={css.statLabel}>{label}</span>
            <span className={css.statValue}>
              {value}
              {unit && value !== '—' && <span className={css.statUnit}>{unit}</span>}
            </span>
          </span>
        </div>
      ))}
    </div>
  );
}

function MonthlyChart({ months }) {
  const max = Math.max(1, ...months.map((d) => d.orders));
  const total = months.reduce((a, d) => a + d.orders, 0);
  return (
    <section className={css.chartCard} aria-label="Aylık sipariş adedi">
      <div className={css.chartHead}>
        <span className={css.chartBar} />
        <h2 className={css.chartTitle}>Aylık Sipariş Adedi</h2>
        <span className={css.chartChip}>son {months.length} ay · {formatNumber(total)} sipariş</span>
      </div>
      <div className={css.bars}>
        {months.map((d, i) => (
          <div key={d.id} className={css.barCol}>
            <span className={css.barValue}>{formatNumber(d.orders)}</span>
            <span className={css.barTrack}>
              <span
                className={`${css.bar} ${d.orders === 0 ? css.barZero : ''}`}
                style={{
                  height: `${Math.max((d.orders / max) * 100, d.orders === 0 ? 2 : 8)}%`,
                  animationDelay: `${i * 55}ms`,
                }}
                title={`${d.label} ${d.year}: ${formatNumber(d.orders)} sipariş`}
              />
            </span>
            <span className={css.barLabel}>{d.label}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function RevenueChart({ months, compare }) {
  const [hover, setHover] = useState(null);
  const W = 320;
  const H = 118;
  const PAD_B = 20;
  const PAD_T = 8;
  const max = Math.max(1, ...months.map((d) => d.amount));
  const stepX = months.length > 1 ? W / (months.length - 1) : W;
  const yOf = (v) => PAD_T + (1 - v / max) * (H - PAD_T - PAD_B);
  const pts = months.map((d, i) => ({ ...d, x: i * stepX, y: yOf(d.amount) }));
  const linePath = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const areaPath = pts.length ? `${linePath} L${W},${H - PAD_B} L0,${H - PAD_B} Z` : '';
  const total = months.reduce((a, d) => a + d.amount, 0);
  const delta = compare ? compare.changePct : null;
  const up = delta !== null && delta >= 0;
  const hp = hover !== null ? pts[hover] : null;

  return (
    <section className={css.chartCard} aria-label="Aylık sipariş tutarı">
      <div className={css.chartHead}>
        <span className={css.chartBar} />
        <h2 className={css.chartTitle}>Aylık Sipariş Tutarı</h2>
        <span className={css.chartChip}>son {months.length} ay · KDV hariç</span>
      </div>
      <div className={css.revWrap}>
        <div>
          <div className={css.revTop}>
            <span className={css.revValue}>{formatTRY(total)}</span>
            {delta !== null && (
              <span className={`${css.revDelta} ${up ? css.revUp : css.revDown}`}>
                {up ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
                %{formatNumber(Math.abs(delta), 1)}
              </span>
            )}
          </div>
          <div className={css.revCaption}>
            {delta !== null
              ? `bu ayın ilk ${compare.compareDay} günü, geçen ayın aynı dönemine göre`
              : `son ${months.length} ay toplamı`}
          </div>
        </div>

        <div className={css.revChart}>
          <svg className={css.revSvg} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none"
               role="img" aria-label="Aylık sipariş tutarı grafiği">
            <defs>
              <linearGradient id="ordRevGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--c-accent)" stopOpacity=".26" />
                <stop offset="100%" stopColor="var(--c-accent)" stopOpacity="0" />
              </linearGradient>
            </defs>
            <line className={css.revGrid} x1="0" y1={H - PAD_B} x2={W} y2={H - PAD_B} />
            <path className={css.revArea} d={areaPath} fill="url(#ordRevGrad)" />
            <path className={css.revLine} d={linePath} />
            {hp && <line className={css.revGuide} x1={hp.x} y1={hp.y} x2={hp.x} y2={H - PAD_B} />}
            {pts.map((p, i) => (
              <circle
                key={p.id}
                className={`${css.revDot} ${i === pts.length - 1 ? css.revDotLast : ''}`}
                cx={p.x} cy={p.y} r={hover === i ? 6 : (i === pts.length - 1 ? 4.5 : 3.2)}
              />
            ))}
            {pts.map((p, i) => (
              <rect
                key={`hit-${p.id}`} className={css.revHit}
                x={p.x - stepX / 2} y={0} width={stepX} height={H}
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(null)}
              />
            ))}
            {pts.map((p, i) => ((i % 2 === 0 || i === pts.length - 1) ? (
              <text key={`t-${p.id}`} className={css.revXLabel}
                    x={Math.min(Math.max(p.x, 10), W - 10)} y={H - 5} textAnchor="middle">
                {p.label}
              </text>
            ) : null))}
          </svg>
          {hp && (
            <div
              className={css.revTip}
              style={{ left: `${(hp.x / W) * 100}%`, top: `${(hp.y / H) * 100}%`, marginTop: '-10px' }}
            >
              <span className={css.revTipMonth}>{hp.label} {hp.year}</span>
              <span className={css.revTipVal}>{formatTRY(hp.amount)}</span>
              <span className={css.revTipMeta}>{formatNumber(hp.orders)} sipariş</span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function LineRow({ line }) {
  return (
    <div className={css.line}>
      <Thumb code={line.code} alt={line.name} className={css.lineThumb} />
      <span className={css.lineBody}>
        <Link className={`${css.lineName} ${css.lineLink}`} to={urunLinki(line.code)} title={line.name}>
          {line.name}
        </Link>
        <span className={css.lineCode}>{line.code}<DepoEtiketleri line={line} /></span>
      </span>
      <span className={css.lineSpacer} />
      <span className={css.lineUnit}>{formatTRY(line.netPrice)}</span>
      <span className={css.lineQty}>×{formatNumber(line.qty)}</span>
      <span className={css.linePrice}>{formatTRY(line.total)}</span>
    </div>
  );
}

function OrderDetail({ order }) {
  const st = ORDER_STATUS[order.status] || ORDER_STATUS.processing;
  const sub = order.total;
  const gross = order.grossTotal > 0 ? order.grossTotal : sub;
  const indirim = Math.max(gross - sub, 0);
  const vat = sub * (VAT_RATE / 100);
  const meta = [
    ['Sipariş Tarihi', `${fmtDateLong(order.date)} ${order.time}`],
    ['Sipariş No', order.no],
    ['ERP Sipariş No', order.erpNos.length ? order.erpNos.join(', ') : '—'],
    ['Durum', st.label],
    ['Depo', depoMetni(order.warehouses)],
  ];

  return (
    <div className={css.detail}>
      <div className={css.dMeta}>
        {meta.map(([k, v]) => (
          <span key={k} className={css.dMetaItem}>
            <span className={css.dMetaLabel}>{k}</span>
            <span className={css.dMetaValue}>{v}</span>
          </span>
        ))}
      </div>

      {order.orderNos.length > 1 && (
        <div className={css.dGroup}>
          <span className={css.dGroupLabel}><Layers size={12} /> İçerdiği Siparişler</span>
          {order.orderNos.map((g) => <span key={g} className={css.dGroupChip}>{g}</span>)}
        </div>
      )}

      <div className={css.dScroll}>
        <table className={css.dTable}>
          <colgroup>
            <col className={css.dColThumb} />
            <col className={css.dColCode} />
            <col className={css.dColName} />
            <col className={css.dColWh} />
            <col className={css.dColQty} />
            <col className={css.dColMoney} />
            <col className={css.dColDisc} />
            <col className={css.dColMoney} />
            <col className={css.dColMoney} />
          </colgroup>
          <thead>
            <tr>
              <th aria-label="Görsel" />
              <th>Ürün Kodu</th>
              <th>Ürün Adı</th>
              <th>Depo</th>
              <th className={css.dCenter}>Adet</th>
              <th className={css.dRight}>Brüt Fiyat</th>
              <th className={css.dCenter}>İndirim</th>
              <th className={css.dRight}>Net Fiyat</th>
              <th className={css.dRight}>Tutar</th>
            </tr>
          </thead>
          <tbody>
            {order.lines.map((l) => (
              <tr key={l.id} className={css.dRow}>
                <td><Thumb code={l.code} alt={l.name} className={css.dThumb} /></td>
                <td><span className={css.dCode}>{l.code}</span></td>
                <td>
                  <Link className={`${css.dName} ${css.lineLink}`} to={urunLinki(l.code)} title={l.name}>
                    {l.name}
                  </Link>
                </td>
                <td data-label="Depo"><DepoEtiketleri line={l} /></td>
                <td className={`${css.dCenter} ${css.dNum}`} data-label="Adet">{formatNumber(l.qty)}</td>
                <td className={`${css.dRight} ${css.dGross}`} data-label="Brüt Fiyat">
                  {formatTRY(l.grossPrice || l.netPrice)}
                </td>
                <td className={css.dCenter} data-label="İndirim">
                  <IndirimHucre line={l} />
                </td>
                <td className={`${css.dRight} ${css.dNum}`} data-label="Net Fiyat">{formatTRY(l.netPrice)}</td>
                <td className={`${css.dRight} ${css.dTotalCell}`} data-label="Tutar">{formatTRY(l.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className={css.dSummary}>
        <div className={css.dSumBox}>
          <div className={css.dSumRow}>
            <span className={css.dSumLabel}>Brüt Toplam</span>
            <span className={css.dSumVal}>{formatTRY(gross)}</span>
          </div>
          {indirim > 0 && (
            <div className={css.dSumRow}>
              <span className={css.dSumLabel}>İndirim</span>
              <span className={css.dSumVal}>−{formatTRY(indirim)}</span>
            </div>
          )}
          <div className={css.dSumRow}>
            <span className={css.dSumLabel}>Ara Toplam</span>
            <span className={css.dSumVal}>{formatTRY(sub)}</span>
          </div>
          <div className={css.dSumRow}>
            <span className={css.dSumLabel}>KDV (%{formatNumber(VAT_RATE)})</span>
            <span className={css.dSumVal}>{formatTRY(vat)}</span>
          </div>
          <span className={css.dSumDivider} />
          <div className={css.dSumGrand}>
            <span className={css.dSumGrandLabel}>Genel Toplam</span>
            <span className={css.dSumGrandVal}>{formatTRY(sub + vat)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function OrderCard({ order, open, onToggle }) {
  const st = ORDER_STATUS[order.status] || ORDER_STATUS.processing;
  const onizleme = order.lines.slice(0, LINE_PREVIEW);
  const kalan = order.lines.length - onizleme.length;

  return (
    <article className={css.order}>
      <div className={css.orderHead}>
        <span className={css.orderDate}>
          <Calendar size={13} />{fmtDate(order.date)}
          <span className={css.orderTime}>{order.time}</span>
        </span>
        <span className={css.orderNo}>{order.no}</span>
        {order.orderNos.length > 1 && (
          <span className={css.groupChip}>
            <Layers size={11} /> Grup Siparişi ({order.orderNos.length} sipariş)
          </span>
        )}
        {order.erpNos.length > 0 && (
          <span className={css.erpChip} title={`ERP: ${order.erpNos.join(', ')}`}>
            <FileText size={11} />{order.erpNos[0]}
            {order.erpNos.length > 1 && ` +${order.erpNos.length - 1}`}
          </span>
        )}
        <span className={css.headSpacer} />
        <span className={`${css.status} ${css[st.tone] || ''}`}>{st.label}</span>
      </div>

      {order.note && (
        <div className={css.noteLine}><MessageSquare size={12} />{order.note}</div>
      )}

      <div className={css.lines}>
        {onizleme.map((l) => <LineRow key={l.id} line={l} />)}
        {kalan > 0 && open === false && (
          <button type="button" className={css.moreLines} onClick={() => onToggle(order.id)}>
            +{kalan} ürün daha
          </button>
        )}
      </div>

      <div className={css.orderFoot}>
        <span className={css.whInfo}><Warehouse size={13} />{depoMetni(order.warehouses)}</span>
        <span className={css.unitInfo}>
          {formatNumber(order.lineCount)} kalem · {formatNumber(order.qty)} adet
        </span>
        <span className={css.totalWrap}>
          <span className={css.totalLabel}>Toplam (KDV hariç)</span>
          <span className={css.totalVal}>{formatTRY(order.total)}</span>
        </span>
        <button type="button" className={css.detailBtn}
                onClick={() => onToggle(order.id)} aria-expanded={open}>
          {open
            ? <>Detayı Gizle <ChevronUp size={13} /></>
            : <>Sipariş Detayı <ChevronDown size={13} /></>}
        </button>
      </div>

      {open && <OrderDetail order={order} />}
    </article>
  );
}

function Pager({ cur, totalPages, totalItems, per, onPage, onPer }) {
  const list = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (cur <= 4) return [1, 2, 3, 4, 5, '…', totalPages];
    if (cur >= totalPages - 3) {
      return [1, '…', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }
    return [1, '…', cur - 1, cur, cur + 1, '…', totalPages];
  }, [cur, totalPages]);

  return (
    <div className={css.pager}>
      <span className={css.pagerInfo}>
        <b>{formatNumber(totalItems)}</b> sipariş · Sayfa {cur}/{totalPages}
      </span>
      <label className={css.perLabel}>
        Sayfa başına
        <select className={css.select} value={per} onChange={(e) => onPer(Number(e.target.value))}>
          {PER_PAGE.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </label>
      {totalPages > 1 && (
        <div className={css.pages}>
          <button type="button" className={css.pageBtn} disabled={cur <= 1}
                  onClick={() => onPage(cur - 1)} aria-label="Önceki sayfa">
            <ChevronLeft size={15} />
          </button>
          {list.map((n, i) => (n === '…' ? (
            <span key={`d${i}`} className={css.dots}>…</span>
          ) : (
            <button key={n} type="button"
                    className={`${css.pageBtn} ${n === cur ? css.pageOn : ''}`}
                    aria-current={n === cur ? 'page' : undefined}
                    onClick={() => onPage(n)}>
              {n}
            </button>
          )))}
          <button type="button" className={css.pageBtn} disabled={cur >= totalPages}
                  onClick={() => onPage(cur + 1)} aria-label="Sonraki sayfa">
            <ChevronRight size={15} />
          </button>
        </div>
      )}
    </div>
  );
}

// ---------- sayfa ----------
export default function OrdersV2() {
  const [sp, setSp] = useSearchParams();
  const [f, setF] = useState(() => urldenOku(sp));
  const [headerTerm, setHeaderTerm] = useState('');
  const [acik, setAcik] = useState({});
  const listeRef = useRef(null);

  const cart = useCartContext();
  const toast = useToast();
  const kurlar = useExchangeRates();
  const handleMenu = useMenuNav(toast.push);
  const ozet = useOrderSummary();
  const liste = useOrders(f);

  useEffect(() => {
    const yeni = urleYaz(f);
    if (yeni !== sp.toString()) setSp(new URLSearchParams(yeni), { replace: true });
  }, [f, sp, setSp]);

  const guncelle = useCallback((yama) => {
    setF((o) => duzelt({ ...o, ...yama, page: 'page' in yama ? yama.page : 1 }));
  }, []);

  const sayfaya = useCallback((n) => {
    guncelle({ page: n });
    if (listeRef.current) listeRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [guncelle]);

  const temizle = useCallback(() => setF((o) => duzelt({ per: o.per })), []);
  const toggle = useCallback((id) => setAcik((a) => ({ ...a, [id]: a[id] ? false : true })), []);

  const filtreSayisi = [f.q, f.period !== 'all', f.wh, f.status].filter(Boolean).length;
  const aylar = ozet.data ? ozet.data.monthly.slice(-CHART_MONTHS) : [];
  const hicSiparisYok = ozet.data && ozet.data.totals.orders === 0;
  const curPage = liste.page || f.page;

  return (
    <div className={styles.page}>
      <ToastStack items={toast.items} onDismiss={toast.dismiss} />
      <Header
        searchTerm={headerTerm}
        onSearchChange={setHeaderTerm}
        cart={cart}
        eurRate={kurlar.eurRate}
        onMenuSelect={handleMenu}
      />

      <main className={styles.main}>
        <div className={css.head}>
          <h1 className={css.title}>
            Siparişlerim
            <span className={css.titleSub}>B2B üzerinden verdiğiniz siparişler</span>
          </h1>
          {ozet.data && (ozet.data.processing > 0 || ozet.data.errors > 0) && (
            <span className={css.headChips}>
              {ozet.data.processing > 0 && (
                <button type="button" className={`${css.headChip} ${css.headChipAmber}`}
                        onClick={() => guncelle({ status: 'processing' })}>
                  <Clock size={12} />{formatNumber(ozet.data.processing)} sipariş işleniyor
                </button>
              )}
              {ozet.data.errors > 0 && (
                <button type="button" className={`${css.headChip} ${css.headChipRed}`}
                        onClick={() => guncelle({ status: 'error' })}>
                  <XCircle size={12} />{formatNumber(ozet.data.errors)} aktarım hatası
                </button>
              )}
            </span>
          )}
        </div>

        <StatCards ozet={ozet.data} />

        {aylar.length > 0 && hicSiparisYok === false && (
          <div className={css.chartGrid}>
            <MonthlyChart months={aylar} />
            <RevenueChart months={aylar} compare={ozet.data.month} />
          </div>
        )}

        <div className={css.filterBar} ref={listeRef}>
          <span className={css.searchBox}>
            <span className={css.searchIcon}><Search size={14} /></span>
            <input
              className={css.searchInput} type="text" value={f.q}
              onChange={(e) => guncelle({ q: e.target.value })}
              placeholder="Sipariş no, ürün kodu veya adı..."
              aria-label="Siparişlerde ara"
            />
            {f.q && (
              <button type="button" className={css.searchClear}
                      onClick={() => guncelle({ q: '' })} aria-label="Aramayı temizle">
                <X size={12} />
              </button>
            )}
          </span>

          <span className={css.filterField}>
            <span className={css.filterLabel}><Calendar size={11} /> Tarih</span>
            <select className={`${css.select} ${f.period !== 'all' ? css.selectOn : ''}`}
                    value={f.period} onChange={(e) => guncelle({ period: e.target.value })}
                    aria-label="Tarih filtresi">
              {PERIODS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </span>

          {f.period === 'custom' && (
            <span className={css.filterField}>
              <input type="date" className={css.dateInput} value={f.from}
                     max={f.to || undefined} aria-label="Başlangıç tarihi"
                     onChange={(e) => guncelle({ from: e.target.value })} />
              <span className={css.filterLabel}>–</span>
              <input type="date" className={css.dateInput} value={f.to}
                     min={f.from || undefined} aria-label="Bitiş tarihi"
                     onChange={(e) => guncelle({ to: e.target.value })} />
            </span>
          )}

          <span className={css.filterField}>
            <span className={css.filterLabel}><Warehouse size={11} /> Depo</span>
            <select className={`${css.select} ${f.wh ? css.selectOn : ''}`}
                    value={f.wh} onChange={(e) => guncelle({ wh: e.target.value })}
                    aria-label="Depo filtresi">
              {WH_F.map((w) => <option key={w.id} value={w.id}>{w.label}</option>)}
            </select>
          </span>

          <span className={css.filterField}>
            <span className={css.filterLabel}><Filter size={11} /> Durum</span>
            <select className={`${css.select} ${f.status ? css.selectOn : ''}`}
                    value={f.status} onChange={(e) => guncelle({ status: e.target.value })}
                    aria-label="Durum filtresi">
              {STATUS_F.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </span>

          <span className={css.barSpacer} />
          <span className={css.resultCount}>
            <b>{formatNumber(liste.totalItems)}</b> sipariş
            {liste.totalItems > 0 && <> · {formatTRY(liste.totalAmount)}</>}
          </span>
          {filtreSayisi > 0 && (
            <button type="button" className={css.clearBtn} onClick={temizle}>
              <X size={13} /> Temizle ({filtreSayisi})
            </button>
          )}
        </div>

        {liste.error && (
          <div className={css.stateBar} role="alert">
            <span>{liste.error}</span>
            <button type="button" className={css.retryBtn} onClick={liste.yenile}>
              <RotateCw size={12} /> Tekrar dene
            </button>
          </div>
        )}

        {liste.loading ? (
          <div className={css.list}>
            {[0, 1, 2].map((i) => <div key={i} className={css.skel} />)}
          </div>
        ) : liste.orders.length === 0 ? (
          <div className={css.empty}>
            <span className={css.emptyIcon}><Inbox size={34} strokeWidth={1.3} /></span>
            <div>
              {filtreSayisi > 0
                ? 'Bu filtrelere uygun sipariş bulunamadı.'
                : 'Henüz B2B siparişiniz bulunmuyor.'}
            </div>
            {filtreSayisi > 0 && (
              <button type="button" className={css.clearBtn} onClick={temizle}>
                <X size={13} /> Filtreleri Temizle
              </button>
            )}
          </div>
        ) : (
          <div className={`${css.list} ${liste.refreshing ? css.fading : ''}`}>
            {liste.orders.map((o) => (
              <OrderCard key={o.id} order={o} open={Boolean(acik[o.id])} onToggle={toggle} />
            ))}
          </div>
        )}

        {liste.loading === false && liste.totalItems > 0 && (
          <Pager
            cur={curPage}
            totalPages={liste.totalPages}
            totalItems={liste.totalItems}
            per={f.per}
            onPage={sayfaya}
            onPer={(n) => guncelle({ per: n })}
          />
        )}
      </main>
      <Footer />
    </div>
  );
}
